import { supabase } from '@/integrations/supabase/client';
import { addMonths, addWeeks, addYears, format } from 'date-fns';

export interface CreateRecurringSeriesParams {
  userId: string;
  description: string;
  type: 'income' | 'expense';
  totalAmount: number;
  categoryId: string | null;
  accountId: string;
  startDate: string;
  recurrenceType: 'installment' | 'fixed';
  installments?: number;
  frequency: 'weekly' | 'monthly' | 'yearly';
  endDate?: string;
}

export interface CreateRecurringSeriesResult {
  recurrenceId: string;
  transactionIds: string[];
}

/**
 * Core logic to create a recurring transaction series.
 * Extracted from useRecurringTransactions for reuse in budget item creation.
 * Returns the recurrence ID and all created transaction IDs.
 */
export async function createRecurringSeriesCore(
  params: CreateRecurringSeriesParams
): Promise<CreateRecurringSeriesResult> {
  const {
    userId,
    description,
    type,
    totalAmount,
    categoryId,
    accountId,
    startDate,
    recurrenceType,
    installments = 1,
    frequency,
    endDate,
  } = params;

  const installmentAmount =
    recurrenceType === 'installment' ? totalAmount / installments : totalAmount;

  const numTransactions = recurrenceType === 'installment' ? installments : 12;

  // Create all transactions in batch
  const transactions = [];
  let currentDate = new Date(startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < numTransactions; i++) {
    const transactionDate = new Date(currentDate);
    transactionDate.setHours(0, 0, 0, 0);

    const isPaid = transactionDate <= today;

    const descWithInstallment =
      recurrenceType === 'installment'
        ? `${description} (${i + 1}/${installments})`
        : description;

    transactions.push({
      user_id: userId,
      account_id: accountId,
      category_id: categoryId || null,
      amount: installmentAmount,
      type,
      description: descWithInstallment,
      transaction_date: format(currentDate, 'yyyy-MM-dd'),
      is_paid: isPaid,
      is_recurring: true,
      is_fixed: recurrenceType === 'fixed',
    });

    // Calculate next date
    if (frequency === 'weekly') {
      currentDate = addWeeks(currentDate, 1);
    } else if (frequency === 'monthly') {
      currentDate = addMonths(currentDate, 1);
    } else {
      currentDate = addYears(currentDate, 1);
    }

    // Break if past end date for fixed recurrences
    if (recurrenceType === 'fixed' && endDate && currentDate > new Date(endDate)) {
      break;
    }
  }

  const { data: insertedTransactions, error: txError } = await supabase
    .from('transactions')
    .insert(transactions)
    .select();

  if (txError) throw txError;
  if (!insertedTransactions?.length) throw new Error('Falha ao criar transações');

  // Create recurrence record linked to first transaction
  const firstTransaction = insertedTransactions[0];
  const { data: recurrence, error: recError } = await supabase
    .from('recurrences')
    .insert({
      user_id: userId,
      transaction_id: firstTransaction.id,
      frequency,
      interval: 1,
      start_date: startDate,
      end_date: endDate || null,
      occurrences: recurrenceType === 'installment' ? installments : null,
    })
    .select('id')
    .single();

  if (recError) throw recError;

  return {
    recurrenceId: recurrence.id,
    transactionIds: insertedTransactions.map((t: any) => t.id),
  };
}

/**
 * Update the amount of all unpaid transactions in a recurring series.
 */
export async function updateRecurringSeriesAmount(
  recurrenceId: string,
  newAmount: number,
  seriesTransactions: { id: string; is_paid: boolean }[]
): Promise<number> {
  const unpaidIds = seriesTransactions
    .filter((t) => !t.is_paid)
    .map((t) => t.id);

  if (unpaidIds.length === 0) return 0;

  const { error } = await supabase
    .from('transactions')
    .update({ amount: newAmount })
    .in('id', unpaidIds);

  if (error) throw error;

  return unpaidIds.length;
}
